import { SpinnerIcon, Icon } from '@phosphor-icons/react';
import React from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import { twMerge } from 'tailwind-merge';
import Tooltip from './Tooltip';

// Define proper button variants
type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  children?: React.ReactNode;
  text?: string; // Keep for backward compatibility
  icon?: Icon;
  iconPosition?: 'left' | 'right';
  iconProps?: {
    color?: string;
    weight?: 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
    size?: number | string;
    className?: string;
  };
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  as?: 'button' | 'a'; // Support for link styling
  href?: string; // When used as link
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  'aria-label'?: string; // For icon-only buttons
  justify?: 'center' | 'left' | 'right';
  /** Shown on hover when the button is disabled. */
  disabledReason?: string;
}

// Design tokens for consistent styling
const buttonTokens = {
  variants: {
    primary:
      'bg-secondary-blue disabled:bg-bluegray-200 data-[disabled]:bg-bluegray-200 hover:bg-primary-lightblue text-primary-lightblue disabled:text-bluegray-500 data-[disabled]:text-bluegray-500 hover:text-secondary-blue',
    outline:
      'border-2 border-border-default disabled:border-bluegray-200 data-[disabled]:border-bluegray-200 bg-transparent text-content-subtle disabled:text-bluegray-400 data-[disabled]:text-bluegray-400 hover:bg-surface-hover',
    ghost:
      'bg-transparent hover:bg-bluegray-100 text-content-subtle disabled:text-bluegray-400 data-[disabled]:text-bluegray-400',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
  },

  icon_variants: {
    default: 'text-content-subtlest',
    error: 'text-content-subtlest'
  },

  sizes: {
    sm: {
      iconSize: 16,
      padding: 'px-1.5 h-[28px]',
      text: 'text-xs',
      iconOnly: 'w-[28px]'
    },
    md: {
      iconSize: 18,
      padding: 'px-2.5 h-[36px]',
      text: 'text-sm',
      iconOnly: 'w-[36px]'
    }
  }
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    text, // Deprecated in favor of children
    icon = null,
    iconPosition = 'left',
    iconProps,
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    disabled = false,
    className = '',
    as = 'button',
    href,
    target,
    rel,
    type = 'button',
    justify = 'center',
    onClick,
    'aria-label': ariaLabel,
    disabledReason,
    ...rest
  },
  ref
) {
  // Content to render
  const content = children || text;
  const isIconOnly = !content && icon;
  const hasIcon = icon && !loading;
  const hasLeftIcon = hasIcon && iconPosition === 'left' && !isIconOnly;
  const hasRightIcon = hasIcon && iconPosition === 'right' && !isIconOnly;
  const isDisabled = disabled || loading;

  // Get size tokens
  const sizeTokens = buttonTokens.sizes[size];

  // Base classes
  const baseClasses = [
    `inline-flex gap-1.5 items-center justify-${justify}`,
    'font-normal rounded-lg',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500',
    'disabled:cursor-not-allowed disabled:pointer-events-none data-[disabled]:cursor-not-allowed',
    fullWidth && 'w-full',
    loading && 'cursor-wait'
  ]
    .filter(Boolean)
    .join(' ');

  // Merge all classes - use icon-only sizing if no content
  const buttonClasses = twMerge(
    baseClasses,
    sizeTokens.padding,
    sizeTokens.text,
    isIconOnly && sizeTokens.iconOnly,
    buttonTokens.variants[variant],
    className
  );

  // Render icon with props
  const renderIcon = () => {
    if (!icon) return null;
    return React.createElement(icon, { size: sizeTokens.iconSize, weight: 'light', ...iconProps });
  };

  const buttonContent = (
    <>
      {isIconOnly ? (
        <span className="flex-shrink-0">
          {loading ? <SpinnerIcon size={sizeTokens.iconSize} className="animate-spin" /> : renderIcon()}
        </span>
      ) : (
        <>
          {loading && <SpinnerIcon size={sizeTokens.iconSize} className="animate-spin mr-2" />}
          {hasLeftIcon && <span className={`flex-shrink-0`}>{renderIcon()}</span>}
          {content && (
            <span data-slot="button-label" className={loading ? 'opacity-75' : ''}>
              {content}
            </span>
          )}
          {hasRightIcon && <span className={`flex-shrink-0`}>{renderIcon()}</span>}
        </>
      )}
    </>
  );

  // Links keep native <a> semantics — Base UI Button must not wrap anchors.
  if (as === 'a' || href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={buttonClasses}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {buttonContent}
      </a>
    );
  }

  const button = (
    <BaseButton
      ref={ref}
      type={type}
      onClick={loading ? undefined : onClick}
      disabled={isDisabled}
      focusableWhenDisabled={loading}
      className={buttonClasses}
      aria-label={ariaLabel}
      title={ariaLabel || (typeof content === 'string' ? content : undefined)}
      aria-busy={loading || undefined}
      {...rest}
    >
      {buttonContent}
    </BaseButton>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement="top">
        <span className="inline-flex">{button}</span>
      </Tooltip>
    );
  }

  return button;
});

export default Button;
export { ButtonVariant, ButtonSize };
